import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { CONTRACT_ADDRESSES } from '../../config/contracts';
import { fmtUSDT, fmtNum } from '../../utils/format';
import AdminSection from './AdminSection';

const Tile = ({ label, value, sub, accent }) => (
  <div className={`admin-tile ${accent || ''}`}>
    <div className="admin-tile-title" style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
    <div className={`card-value ${accent === 'yellow' ? 'yellow' : accent === 'green' ? 'green' : ''}`}>{value}</div>
    {sub && <div className="card-sub">{sub}</div>}
  </div>
);

const safeBN = (v) => { try { return ethers.BigNumber.from(v || 0); } catch { return ethers.BigNumber.from(0); } };

const AdminStats = ({ role, refreshKey }) => {
  const { coreStorage, usdt } = useContracts();
  const [stats, setStats] = useState({
    totalUsers: '0',
    plansBalance: '0',
    totalPoolFunds: '0',
    funds: { elite: '0', pro: '0', global: '0' },
    rounds: { elite: '0', pro: '0', global: '0' }
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read || !usdt?.read) return;
      try {
        const [totalUsers, plansBalance, rounds, funds] = await Promise.all([
          coreStorage.read.getTotalUsers().catch(() => 0),
          usdt.read.balanceOf(CONTRACT_ADDRESSES.PLANS).catch(() => 0),
          coreStorage.read.getCurrentRounds().catch(() => ({ elite: 0, pro: 0, global: 0 })),
          coreStorage.read.getPoolFunds().catch(() => ({ elite: 0, pro: 0, global: 0 }))
        ]);

        const totalPoolFunds = safeBN(funds.elite).add(safeBN(funds.pro)).add(safeBN(funds.global));

        if (!cancelled) {
          setStats({
            totalUsers: totalUsers.toString(),
            plansBalance: plansBalance.toString(),
            totalPoolFunds: totalPoolFunds.toString(),
            funds: {
              elite: funds.elite.toString(),
              pro: funds.pro.toString(),
              global: funds.global.toString()
            },
            rounds: {
              elite: rounds.elite.toString(),
              pro: rounds.pro.toString(),
              global: rounds.global.toString()
            }
          });
        }
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [usdt, coreStorage, refreshKey]);

  return (
    <AdminSection
      title="System Overview"
      subtitle="Live contract metrics."
      role={role}
    >
      {/* Top tier — system-wide */}
      <div className="admin-grid-3">
        <Tile label="Total Users"            value={fmtNum(stats.totalUsers)}     sub="Registered on-chain" accent="yellow" />
        <Tile label="Plans Contract Balance" value={fmtUSDT(stats.plansBalance)}  sub="USDT held"           accent="green" />
        <Tile label="Pool Funds (Total)"     value={fmtUSDT(stats.totalPoolFunds)} sub="USDT awaiting distribution" accent="yellow" />
      </div>

      {/* Per-pool fund + round */}
      <div className="admin-grid-3" style={{ marginTop: 12 }}>
        <Tile label="Elite Pool Fund"  value={fmtUSDT(stats.funds.elite)}  sub={`Round #${stats.rounds.elite}`}   accent="yellow" />
        <Tile label="Pro Pool Fund"    value={fmtUSDT(stats.funds.pro)}    sub={`Round #${stats.rounds.pro}`}     accent="green" />
        <Tile label="Global Pool Fund" value={fmtUSDT(stats.funds.global)} sub={`Round #${stats.rounds.global}`}  accent="yellow" />
      </div>
    </AdminSection>
  );
};

export default AdminStats;
